import produce from 'immer';
import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ReactComponent as Logo } from '../../assets/images/logo.svg';
import useAuth from '../../hooks/auth/useAuth';
import '../../styles/auth.css';
import AuthFormItemWrapper from './form-item-wrapper';

interface IFormField {
    error: string;
    value: string | boolean;
}

export type TRegistrationFormFields = {
    userName: IFormField;
    mail: IFormField;
    password: IFormField;
    passwordConfirm: IFormField;
    confirmation: IFormField;
    invite?: IFormField;
};

type TFields = keyof TRegistrationFormFields;

function Registration() {
    const [searchParams] = useSearchParams();
    const [fields, setFields] = useState<TRegistrationFormFields>({
        userName: {
            error: '',
            value: '',
        },
        mail: {
            error: '',
            value: '',
        },
        password: {
            error: '',
            value: '',
        },
        passwordConfirm: {
            error: '',
            value: '',
        },
        confirmation: {
            error: '',
            value: false,
        },
    });
    const emailFromParams = searchParams.get('email');
    const inviteTokenFromParams = searchParams.get('invite');

    const { register } = useAuth();

    const compareFields = (data: TRegistrationFormFields, value: string | boolean, compareKey?: TFields) => (typeof compareKey === 'string' ? value === data[compareKey]?.value : true);

    const getFormControlClass = (error: string) => {
        if (error.length > 0) {
            return 'form-control is-invalid';
        }
        return 'form-control';
    };

    const onAgreeTermsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { checked } = e.target;
        setFields(produce(fields, (draft) => {
            draft.confirmation.value = checked;
        }));
    };

    const onInputChange = (e: React.ChangeEvent<HTMLInputElement>, key: TFields, compareKey?: TFields, compareError?: string) => {
        const { value, validationMessage } = e.target;
        setFields(produce(fields, (draft) => {
            draft[key] = {
                error: (compareFields(draft, value, compareKey) ? validationMessage : compareError) || '',
                value,
            };
        }));
    };

    const onRegistrationClick = async () => {
        let errorForm = false;

        setFields(produce(fields, (draft) => {
            const keys = Object.keys(draft) as (keyof TRegistrationFormFields)[];
            keys.forEach((key) => {
                const feild = draft[key];
                if (feild instanceof Object) {
                    if (feild.error.length > 0) {
                        errorForm = true;
                    }
                    if (feild.value === '') {
                        errorForm = true;
                        feild.error = 'Поле является обязательным';
                    }
                }
            });
        }));

        if (!errorForm) {
            const errorFields = await register(fields);
            if (errorFields && typeof errorFields === 'object') {
                setFields(produce(fields, (draft) => {
                    const keys = Object.keys(errorFields) as TFields[];
                    keys.forEach((key) => {
                        const field = draft[key];
                        if (field) {
                            field.error = errorFields[key];
                        }
                    });
                }));
            }
        }
    };

    useEffect(() => {
        setFields(produce(fields, (draft) => {
            if (typeof emailFromParams === 'string') {
                draft.mail = {
                    error: '',
                    value: emailFromParams,
                };
            }
            if (typeof inviteTokenFromParams === 'string') {
                draft.invite = {
                    error: '',
                    value: inviteTokenFromParams,
                };
            }
        }));
    }, [emailFromParams, inviteTokenFromParams]);

    return (
        <div className="login-page">
            <a className="mb-4 login-app-logo" href="/">
                <Logo />
            </a>
            <div className="card">
                <div className="card-body">
                    <p className="login-box-msg"><b>Регистрация</b></p>
                    <form>
                        <AuthFormItemWrapper error={fields.userName.error}>
                            <input
                                type="text"
                                className={getFormControlClass(fields.userName.error)}
                                placeholder="ФИО"
                                onChange={(e) => onInputChange(e, 'userName')}
                            />
                            <div className="input-group-append">
                                <div className="input-group-text">
                                    <span className="fas fa-user" />
                                </div>
                            </div>
                        </AuthFormItemWrapper>
                        <AuthFormItemWrapper error={fields.mail.error}>
                            <input
                                required
                                type="email"
                                className={getFormControlClass(fields.mail.error)}
                                placeholder="Электронная почта"
                                onChange={(e) => onInputChange(e, 'mail')}
                                value={fields.mail.value as string}
                                readOnly={typeof emailFromParams === 'string'}
                            />
                            <div className="input-group-append">
                                <div className="input-group-text">
                                    <span className="fas fa-envelope" />
                                </div>
                            </div>
                        </AuthFormItemWrapper>
                        <AuthFormItemWrapper error={fields.password.error}>
                            <input
                                type="password"
                                className={getFormControlClass(fields.password.error)}
                                placeholder="Пароль"
                                minLength={6}
                                onChange={(e) => onInputChange(e, 'password')}
                            />
                            <div className="input-group-append">
                                <div className="input-group-text">
                                    <span className="fas fa-lock" />
                                </div>
                            </div>
                        </AuthFormItemWrapper>
                        <AuthFormItemWrapper error={fields.passwordConfirm.error}>
                            <input
                                type="password"
                                className={getFormControlClass(fields.passwordConfirm.error)}
                                placeholder="Повторить пароль"
                                minLength={6}
                                onChange={(e) => onInputChange(e, 'passwordConfirm', 'password', 'Пароли не совпадают')}
                            />
                            <div className="input-group-append">
                                <div className="input-group-text">
                                    <span className="fas fa-lock" />
                                </div>
                            </div>
                        </AuthFormItemWrapper>
                        <AuthFormItemWrapper error={fields.confirmation.error}>
                            <div className="icheck-primary">
                                <input type="checkbox" id="agree-terms" name="terms" value="agree" onChange={onAgreeTermsChange} />
                                <label id="agree-terms-label" htmlFor="agree-terms">
                                    Я согласен с условиями
                                    {' '}
                                    <a href="/">политики конфиденциальности</a>
                                </label>
                            </div>
                        </AuthFormItemWrapper>
                        <button
                            type="button"
                            className="btn btn-primary col-12"
                            onClick={onRegistrationClick}
                            disabled={!fields.confirmation.value}
                        >
                            Зарегистрироваться
                        </button>
                        <div className="mt-2 text-center">
                            <Link to="/login">
                                Я уже зарегистрирован
                            </Link>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default Registration;
